/**
 * Appointment Domain Types
 *
 * Mirrors backend domain types for appointments, schedules, and slots
 */

// =============================================================================
// Appointment Status
// =============================================================================

export const APPOINTMENT_STATUSES = {
  REQUESTED: 'REQUESTED',
  SCHEDULED: 'SCHEDULED',
  CONFIRMED: 'CONFIRMED',
  CHECKED_IN: 'CHECKED_IN',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  NO_SHOW: 'NO_SHOW',
  RESCHEDULED: 'RESCHEDULED',
} as const

export type AppointmentStatus = (typeof APPOINTMENT_STATUSES)[keyof typeof APPOINTMENT_STATUSES]

export const APPOINTMENT_STATUS_LABELS: Record<AppointmentStatus, string> = {
  REQUESTED: 'Solicitada',
  SCHEDULED: 'Programada',
  CONFIRMED: 'Confirmada',
  CHECKED_IN: 'En Recepción',
  IN_PROGRESS: 'En Curso',
  COMPLETED: 'Completada',
  CANCELLED: 'Cancelada',
  NO_SHOW: 'No Asistió',
  RESCHEDULED: 'Reprogramada',
}

export const APPOINTMENT_STATUS_COLORS: Record<AppointmentStatus, string> = {
  REQUESTED: 'secondary',
  SCHEDULED: 'default',
  CONFIRMED: 'default',
  CHECKED_IN: 'warning',
  IN_PROGRESS: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'destructive',
  NO_SHOW: 'destructive',
  RESCHEDULED: 'secondary',
}

// =============================================================================
// Booked Via
// =============================================================================

export const BOOKED_VIA = {
  STAFF: 'STAFF',
  PATIENT_PORTAL: 'PATIENT_PORTAL',
} as const

export type BookedVia = (typeof BOOKED_VIA)[keyof typeof BOOKED_VIA]

export const BOOKED_VIA_LABELS: Record<BookedVia, string> = {
  STAFF: 'Personal',
  PATIENT_PORTAL: 'Portal Paciente',
}

// =============================================================================
// Day of Week
// =============================================================================

export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6

export const DAY_NAMES: Record<DayOfWeek, string> = {
  0: 'Domingo',
  1: 'Lunes',
  2: 'Martes',
  3: 'Miércoles',
  4: 'Jueves',
  5: 'Viernes',
  6: 'Sábado',
}

export const DAY_NAMES_SHORT: Record<DayOfWeek, string> = {
  0: 'Dom',
  1: 'Lun',
  2: 'Mar',
  3: 'Mié',
  4: 'Jue',
  5: 'Vie',
  6: 'Sáb',
}

// =============================================================================
// Provider Schedule
// =============================================================================

export interface ProviderSchedule {
  id: string
  providerId: string
  modalityId: string
  dayOfWeek: DayOfWeek
  startTime: string
  endTime: string
  slotDurationMinutes: number
  maxConcurrent: number
  isActive: boolean
  validFrom: string | null
  validUntil: string | null
  institutionId: string | null
  createdAt: string
  updatedAt: string
}

export interface CreateProviderScheduleData {
  providerId: string
  modalityId: string
  dayOfWeek: number
  startTime: string
  endTime: string
  slotDurationMinutes: number
  maxConcurrent?: number
  validFrom?: string
  validUntil?: string
  institutionId?: string | null
}

export interface UpdateProviderScheduleData {
  startTime?: string
  endTime?: string
  slotDurationMinutes?: number
  maxConcurrent?: number
  isActive?: boolean
  validFrom?: string | null
  validUntil?: string | null
}

export interface ProviderScheduleSearchFilters {
  providerId?: string
  modalityId?: string
  dayOfWeek?: number
  isActive?: boolean
  institutionId?: string
  limit?: number
  offset?: number
}

// =============================================================================
// Schedule Override
// =============================================================================

export interface ScheduleOverride {
  id: string
  providerId: string
  overrideDate: string
  isAvailable: boolean
  startTime: string | null
  endTime: string | null
  slotDurationMinutes: number | null
  reason: string | null
  institutionId: string | null
  createdAt: string
}

export interface CreateScheduleOverrideData {
  providerId: string
  overrideDate: string
  isAvailable: boolean
  startTime?: string
  endTime?: string
  slotDurationMinutes?: number
  reason?: string
  institutionId?: string | null
}

// =============================================================================
// Appointment
// =============================================================================

export interface Appointment {
  id: string
  institutionId: string | null
  patientId: string
  providerId: string
  modalityId: string
  scheduledDate: string
  scheduledTime: string
  endTime: string
  status: AppointmentStatus
  studyId: string | null
  bookedVia: BookedVia
  notes: string | null
  cancellationReason: string | null
  rescheduledFromId: string | null
  rescheduledToId: string | null
  createdBy: string | null
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

export interface AppointmentWithDetails extends Appointment {
  patientName: string
  patientDocument: string
  providerName: string
  modalityCode: string
  modalityName: string
}

export interface CreateAppointmentData {
  patientId: string
  providerId: string
  modalityId: string
  scheduledDate: string
  scheduledTime: string
  endTime: string
  bookedVia?: BookedVia
  notes?: string
  institutionId?: string | null
}

export interface AppointmentSearchFilters {
  patientId?: string
  providerId?: string
  modalityId?: string
  status?: AppointmentStatus
  dateFrom?: string
  dateTo?: string
  bookedVia?: BookedVia
  institutionId?: string
  limit?: number
  offset?: number
}

export interface RescheduleData {
  scheduledDate: string
  scheduledTime: string
  endTime: string
  reason?: string
}

// =============================================================================
// Time Slots
// =============================================================================

export interface TimeSlot {
  time: string
  endTime: string
  available: boolean
  remainingCapacity: number
}

export interface AvailableDay {
  date: string
  totalSlots: number
  availableSlots: number
  isBlocked: boolean
}

// =============================================================================
// Mi agenda
// =============================================================================

export interface ConteoPorModalidad {
  codigo: string
  nombre: string
  cantidad: number
}

/**
 * El resumen que un profesional mira antes de empezar el día.
 *
 * `enPie` es lo que de verdad hay que atender: las canceladas van aparte a
 * propósito, porque contarlas haría parecer la agenda más llena de lo que está.
 */
export interface ResumenDeAgenda {
  enPie: number
  canceladas: number
  noAsistieron: number
  atendidas: number
  porModalidad: ConteoPorModalidad[]
  porDia: { fecha: string; enPie: number }[]
}

export interface MiAgenda {
  citas: AppointmentWithDetails[]
  resumen: ResumenDeAgenda
}
